import { Component, Input, OnInit } from "@angular/core";
import { IGetVideoEntryOptions } from "@app/main/components/videos/models/IGetVideoEntryOptions";
import { CameraService } from "@app/main/components/videos/services/camera.service";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { VideoEventCodeService } from "@app/main/components/videos/services/video-event-code.service";
import { VideoEntryUtils } from "@app/main/components/videos/utils/video-entry-utils";
import { NavigationService } from "@app/services/navigation.service";
import { CameraDisplayService } from "@components/videos/cameraDisplay.service";
import { VideoEventCode } from "@components/videos/enums/VideoEventCode";
import { VideoEventRisk } from "@components/videos/enums/VideoEventRisk";
import { VideoStatus } from "@components/videos/enums/VideoStatus";
import { IVideoEntry } from "@components/videos/models/IVideoEntry";
import { IVTableColumn } from "@directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@directives/_layout/v-table/_services/v-table.service";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { ProgressColourPalette, VProgressBar } from "@directives/_misc/v-progress-bar/v-progress-bar.component";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { VerticalFilterItemCreationCallback } from "@directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component";
import { DateRange } from "@enums/DateRange.enum";
import { ResourceType } from "@enums/ResourceType";
import { SortDirection } from "@enums/SortDirection";
import { IDriver } from "@interfaces/IDriver";
import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { IVehicle } from "@interfaces/IVehicle";
import { IVideoEventCategory } from "@interfaces/IVideo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { CanvasService, IPieDataSeries } from "@services/canvas.service";
import { ColourService } from "@services/colour.service";
import { GeocodeService } from "@services/geocode.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { ClusterIcon, IMarkerOptions } from "@typings/google.maps.angular";
import { CameraModule } from "../../camera.module.ajs";
import { VideoAddressComponent } from "../video-address/video-address.component";
import { VideoDriverAssignmentComponent } from "../video-driver-assignment/video-driver-assignment.component";
import { VideoLabelIcon } from "@app/main/components/videos/enums/video-label-icon";
import { VehicleService } from "@app/services/vehicle.service";
import { VehicleType } from "@app/enum/VehicleType.enum";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { VideoDownloadIcons } from "@app/enum/VideoDownloadIcons";
import "./video-event-list.component.scss";

@UntilDestroy()
@Component({
    selector: "video-event-list",
    template: require("./video-event-list.component.html")
})
export class VideoEventListComponent implements OnInit {

    @Input() vehicle: IVehicle;
    @Input() driver: IDriver;

    videoEvents: IVideoEntry[];
    displayVideoEvents: IVideoEntry[];

    veriMap: IVeriMapComponent;
    eventMarkerOptions: IMarkerOptions<IGoogleMapsMarker<IVideoEntry>>;
    eventCategories: IVideoEventCategory[] = [];

    filterTabs: IFilterBarTab<IVideoEntry>[] = [];

    defaultDateRange = DateRange.Latest7Days;
    filterEvent: IAggregateFilterEvent<IVideoEntry>;

    showMinorEvents: boolean;

    loading: boolean;

    exportedFileName: string = this.translationService.t("VideoEventList.VideoEvents");

    //// #Tables
    tableColumns: IVTableColumn<IVideoEntry>[];
    tableRows: IVTableRow<IVideoEntry>[];

    // ---

    VideoStatus = VideoStatus;

    constructor(
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly colourService: ColourService,
        private readonly canvasService: CanvasService,
        private readonly vehicleService: VehicleService,
        private readonly navigationService: NavigationService,
        private readonly veriDate: VeriDate,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService,
        private readonly geocodeService: GeocodeService,
        private readonly videoEventCodeService: VideoEventCodeService,
        private readonly cameraService: CameraService,
        private readonly videoEntryService: VideoEntryService) {
        this.createTabs();
    }

    ngOnInit(): void {
        this.configureEventMarkerOptions();
        this.createTable();
        this.videoEntryService.videoUpdate$.pipe(untilDestroyed(this)).subscribe(videoEntry => {
            this.addVideoEntryToCurrentEntries(videoEntry);
        })
    }

    onRowClicked(row: IVTableRow<IVideoEntry>): void {
        const event = row.data;
        this.goToEvent(event);
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.veriMap = veriMap;
    }

    onFilterChanged(event: IAggregateFilterEvent<IVideoEntry>): void {
        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.getVideoEvents(true);
            return;
        }

        this.filterVideos();
    }

    eventItemCreationCallback: VerticalFilterItemCreationCallback<IVideoEntry> = (videoEvent) => {

        return {
            name: videoEvent.displayEventCode,
            icon: this.cameraService.getIconForEvent(videoEvent.displayEventCode),
            data: videoEvent,
            color: videoEvent.color.solidColour,
            predicate: (d) => d.EventCode === videoEvent.EventCode
        };
    }

    vehicleItemCreationCallback: VerticalFilterItemCreationCallback<IVideoEntry> = (videoEvent) => {
        return {
            name: videoEvent.vehicle.Reg,
            tooltip: videoEvent.vehicle.ShortReg,
            icon: this.vehicleService.getIconForVehicleType(videoEvent.vehicle.VehicleType),
            data: videoEvent,
            predicate: (d: IVideoEntry) => d.VehicleId === videoEvent.VehicleId
        };
    }

    driverItemCreationCallback: VerticalFilterItemCreationCallback<IVideoEntry> = (videoEvent) => {
        return {
            name: videoEvent.driver?.DisplayName || this.translationService.t("FleetEvents.UnknownDriver"),
            icon: VideoLabelIcon.Driver,
            data: videoEvent,
            predicate: (d: IVideoEntry) => d.DriverId === videoEvent.DriverId
        };
    }

    // ---

    private getVideoEvents(invalidateCache = false, showLoadingIndicator = true): void {
        if (!this.filterEvent) {
            return;
        }

        const options: IGetVideoEntryOptions = {
            processEntries: true,
            invalidateCache: invalidateCache,
            includeArchived: true
        };

        if (this.vehicle || this.filterEvent.vehicle) {
            options.vehicleFilter = { vehicle: this.vehicle || this.filterEvent.vehicle };
        }

        if (this.driver || this.filterEvent.driver) {
            options.driverFilter = { driver: this.driver || this.filterEvent.driver };
        }

        if (this.filterEvent.group) {
            options.groupFilter = { group: this.filterEvent.group, resourceType: ResourceType.Any };
        }

        this.loading = showLoadingIndicator;

        this.videoEntryService.getVideosEntriesWithMappings$(
            this.filterEvent.date[0],
            this.filterEvent.date[1],
            options
        )
            .pipe(
                untilDestroyed(this)
            )
            .subscribe({
                next: (videos) => {
                    this.videoEvents = videos;
                    this.filterVideos();
                    this.processEventCategories();
                    this.createTableRows();
                    this.loading = false;
                },
                error: (error) => {
                    this.loading = false;
                    console.error(error);
                }
            });
    }

    private filterVideos(): void {
        let filterData = this.filterEvent?.filteredData
        if (!filterData) {
            filterData = this.videoEvents;
        }
        this.displayVideoEvents = filterData?.filter(event => event.color && event.Latitude && event.Longitude);

        this.createMarkers();
    }

    // Move to service and let videoList use it also
    filterVideoBySearchText(v: IVideoEntry, text: string): boolean {
        if (v.vehicle.ShortReg.toLocaleLowerCase().indexOf(text) >= 0 || v.vehicle.Reg.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        if (v.driver?.Name.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }

    private createMarkers(): void {
        if (!this.displayVideoEvents) {
            return;
        }

        const pipCache = new Map<string, IGoogleMapsMarkerOptions>();

        this.displayVideoEvents.forEach(event => {
            if (!pipCache.has(event.color.solidColour)) {
                pipCache.set(event.color.solidColour, this.generateEventMarkerPipOptions(event));
            }

            if (!event.markerModel) {
                event.markerModel = {
                    id: event.Id,
                    options: copy(pipCache.get(event.color.solidColour)),
                    model: event
                }

                event.markerModel.options.title = `${event.vehicle.Reg} - ${event.displayEventCode} - ${this.veriDate.formatDate(event.DateCreated)}`;
                event.markerModel.options.position = { lat: event.Latitude, lng: event.Longitude }
            }
        });
    }

    private generateEventMarkerPipOptions(event: IVideoEntry): IGoogleMapsMarkerOptions {
        let options: IGoogleMapsMarkerOptions = {
            isPip: true,
        };

        const width = 16
        const icon: google.maps.Icon = {
            url: this.veriMarkerService.generateColorPip(event.color.solidColour, width),
            scaledSize: new google.maps.Size(width, width),
            anchor: new google.maps.Point(width / 2, width / 2),
        }

        options = {
            icon: icon,
            zIndex: 0
        };

        return options;
    }

    private configureEventMarkerOptions(): void {
        this.eventMarkerOptions = {
            events: {},
            control: {},
            type: "cluster",
            typeOptions: {
                imagePath: "https://rawgit.com/googlemaps/js-marker-clusterer/gh-pages/images/m",
                maxZoom: 13,
                gridSize: 40 // default 60
            },
            typeEvents: {}
        };

        this.eventMarkerOptions.typeEvents.postClusterShow = (clusterIcon) => { this.createClusterIcon(clusterIcon); }
        this.eventMarkerOptions.events.click = (marker, eventName, model) => this.goToEvent(model.model);
    }

    private goToEvent(event: IVideoEntry): void {
        if (event.Mp4VideoFileGuid && event.Status === VideoStatus.Downloaded) {
            this.cameraDisplayService.showVideoPlayer(event);
            return;
        }

        this.navigationService.go(CameraModule.EventView, { eventId: event.Id });
    }

    private createClusterIcon(clusterIcon: ClusterIcon): void {
        const markers = clusterIcon.cluster_.markers_;
        const eventArray: IVideoEntry[] = markers.map(marker => marker.model);
        const matchingEventCategories = this.getEventCategoriesForEvents(eventArray);

        const data: IPieDataSeries[] = [];

        matchingEventCategories.forEach(category => {
            data.push({
                value: category.events.length,
                color: category.color.solidColour
            });
        });

        const pieCanvas = this.canvasService.getPieCanvas(clusterIcon.width_, data);
        clusterIcon.div_.appendChild(pieCanvas);
    }

    private getEventCategoriesForEvents(events: IVideoEntry[]): IVideoEventCategory[] {
        const categories: IVideoEventCategory[] = [];

        events.forEach(event => {
            if (event.eventCategory && categories.filter(category => category == event.eventCategory).length == 0) {
                categories.push(event.eventCategory);
            }
        });

        // Sort by size
        categories.sort((a, b) => b.events.length - a.events.length);

        return categories;
    }

    private processEventCategories(): void {
        this.eventCategories = [];

        const numPossibleVideoEvents = Object.keys(VideoEventCode).length / 2;

        this.videoEvents.forEach(event => {
            let existingcategory: IVideoEventCategory = this.eventCategories.find(eventcategory => eventcategory.name === event.displayEventCode);

            if (!existingcategory) {
                existingcategory = {
                    id: event.EventCode,
                    name: event.displayEventCode,
                    count: 0,
                    events: [],
                    icon: this.cameraService.getIconForEvent(event.displayEventCode),
                    color: {
                        solidColour: this.colourService.getVigetColor(event.EventCode, numPossibleVideoEvents),
                        description: ""
                    }
                }

                if (!existingcategory.color.solidColour) {
                    existingcategory.color.solidColour = "#adadad"; // solid colour of rgba(0, 0, 0, 0.3)
                }

                this.eventCategories.push(existingcategory);
            }

            event.eventCategory = existingcategory;
            event.color = existingcategory.color;

            existingcategory.events.push(event);
            existingcategory.count = existingcategory.events.length;
        });
    }

    private addVideoEntryToCurrentEntries(video: IVideoEntry): void {
        this.videoEvents = [
            ...this.videoEvents.filter(e => e.Id !== video.Id),
            video
        ];

        this.createTableRows();
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.videoEvents, this.tableColumns);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'Common.Time', minTableWidth: 700, sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (event) => this.vTableService.createDateCell(event.VideoStartTime)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (event) => this.vTableService.createVehicleRegCell(event.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Driver' }),
                createCellFunction: (event) => {
                    return this.vTableService.createTableCell(
                        event.driver?.DisplayName || "--",
                        {
                            classes: !event.driver ? 'text-muted' : '',
                            component: VideoDriverAssignmentComponent,
                            componentInputs: {
                                videoEntry: event
                            }
                        })
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Event", width: 140, minTableWidth: 400 }),
                createCellFunction: (event) => this.vTableService.createTableCell(event.displayEventCode)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VideoEventList.Risk", width: 100, minTableWidth: 700 }),
                createCellFunction: (event) => this.vTableService.createEnumCell(VideoEventRisk, "VideoEventRisk", this.videoEventCodeService.getEventRisk(event.EventCode))

            },
            {
                header: this.vTableService.createLocationInformationHeader({ minTableWidth: 700, width: null }),
                createCellFunction: (event) => this.vTableService.createTableCell(event, { exportDataMethod: () => this.exportVideoEntryLocationMethod(event), component: VideoAddressComponent, componentInputs: { videoEntry: event } })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Videos.Download", minTableWidth: 950, width: 110 }),
                createCellFunction: (event) => {
                    const auditLog = event.MetaData?.AuditLogs?.[0] ?? "";
                    
                    var icons = [];

                    if (auditLog.includes("Auto Download")) {
                        icons.push(VideoDownloadIcons.AutoDownload);
                    }

                    if (auditLog.includes("Requested by AddSecure Team")) {
                        icons.push(VideoDownloadIcons.FoundationDownload);
                    }

                    if (VideoEntryUtils.isDeleted(event)) {
                        return this.vTableService.createVideoDownloadStatusCell(this.translationService.t("VideoEventList.Deleted"), icons);
                    }

                    if (event.Status === VideoStatus.Archived) {
                        return this.vTableService.createVideoDownloadStatusCell(this.translationService.t("VideoEventList.Archived"), icons);
                    }

                    if (VideoEntryUtils.isDownloaded(event)) {
                        return this.vTableService.createVideoDownloadStatusCell(this.translationService.t("VideoEventList.Complete"), icons);
                    }

                    if (VideoEntryUtils.isDownloading(event)) {
                        return this.vTableService.createTableCell(event.MetaData?.DownloadProgress, { component: VProgressBar, componentInputs: { palette: ProgressColourPalette.RAG, percentage: event.MetaData?.DownloadProgress } })
                    }

                    if (VideoEntryUtils.isRequested(event)) {
                        return this.vTableService.createVideoDownloadStatusCell(this.translationService.t("cameraService.Requested"), icons);
                    }

                    return this.vTableService.createTableCell(null)
                }
            }
        ];
    }

    private exportVideoEntryLocationMethod(videoEntry: IVideoEntry): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!videoEntry) {
                return resolve("N/A")
            }

            if (videoEntry.GeocodedAddressId) {
                return resolve(videoEntry.PlaceName);
            }

            return this.geocodeService.getLocationInformationFromVideo(videoEntry.Id).then(location => {
                return resolve(location.LocationDescription);
            }).catch((error) => {
                console.error("Error retrieving location: ", error);

                return resolve("")
            });
        })

    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition('VideoEventList.AllEvents', v => VideoEntryUtils.isEventAndActive(v) && v.Status != VideoStatus.Restricted && v.EventCode != VideoEventCode.Unknown && v.EventCode != VideoEventCode.Exception);
        this.createTabDefinition('VideoEventList.RestrictedEvents', v => VideoEntryUtils.isEventAndActive(v) && v.Status == VideoStatus.Restricted && v.EventCode != VideoEventCode.Unknown && v.EventCode != VideoEventCode.Exception);
        this.createTabDefinition('VideoEventList.Downloaded', v => v.Status == VideoStatus.Downloaded);
        this.createTabDefinition('VideoEventList.ArchivedEvents', v => v.Status == VideoStatus.Archived);
    }

    private createTabDefinition(translationString: string, callback: (video: IVideoEntry) => boolean): void {
        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }
}
